import { logger } from "@/utils/monitoring/logging"

import { dexie } from "../db"
import { modifyOrderedStep } from "../tables/orderedSteps/orderedSteps"
import { getPhotosByStep, softDeletePhoto } from "../tables/photos/photos"
import { logDexieError } from "../utils/logDexieError"

type DeleteClaimPhotoCopiesParams = {
  sourcePhotoId: string
  sessionId: string
}

/**
 * Removes the view photos copied from a deleted Claim photo and reopens every covered step
 * left without a photo, so the flow asks for that view again.
 */
export async function deleteClaimPhotoCopies(params: DeleteClaimPhotoCopiesParams): Promise<void> {
  const { sourcePhotoId, sessionId } = params

  try {
    await dexie.transaction("rw", [dexie.photos, dexie.photoBlobs, dexie.orderedSteps], async () => {
      // `sourcePhotoId` is not indexed, so the session index narrows the scan
      const copies = await dexie.photos
        .where("sessionId")
        .equals(sessionId)
        .and((photo) => photo.sourcePhotoId === sourcePhotoId && !photo.deleted)
        .toArray()

      for (const copy of copies) {
        await softDeletePhoto(copy.id)

        // Keeps the step finished when the user already took their own photo for it
        const remainingPhotos = await getPhotosByStep(sessionId, copy.type)
        if (remainingPhotos.length === 0) {
          await modifyOrderedStep(copy.type, sessionId, { isFinished: false })
          logger.info(`[claim] Reopened step ${copy.type} after removing claim photo copy ${copy.id}`)
        }
      }
    })
  } catch (error) {
    logDexieError(error)
  }
}
